/**
 * WAI-ARIA tabs keyboard support for Bootstrap tab components.
 * Manual activation: arrow keys move focus; Enter/Space activate the focused tab.
 */
import { onReady } from './on-ready.js';

const TAB_SELECTOR = '[role="tab"]';

function getTabs(tablist) {
    return Array.from(tablist.querySelectorAll(TAB_SELECTOR));
}

function getSelectedTab(tabs) {
    return tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
}

function setTabFocus(tablist, focusedTab) {
    getTabs(tablist).forEach((tab) => {
        tab.setAttribute('tabindex', tab === focusedTab ? '0' : '-1');
    });
    focusedTab.focus();
}

function setSelectedTab(tablist, selectedTab) {
    getTabs(tablist).forEach((tab) => {
        const isSelected = tab === selectedTab;
        tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        tab.setAttribute('tabindex', isSelected ? '0' : '-1');
    });
}

function isVerticalTablist(tablist) {
    return tablist.getAttribute('aria-orientation') === 'vertical';
}

function handleTablistKeydown(event, tablist) {
    const tabs = getTabs(tablist);
    const currentIndex = tabs.indexOf(event.target);

    if (currentIndex === -1) {
        return;
    }

    const vertical = isVerticalTablist(tablist);
    let nextIndex = null;

    switch (event.key) {
        case 'ArrowLeft':
            if (vertical) {
                return;
            }
            event.preventDefault();
            nextIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
            setTabFocus(tablist, tabs[nextIndex]);
            break;
        case 'ArrowRight':
            if (vertical) {
                return;
            }
            event.preventDefault();
            nextIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
            setTabFocus(tablist, tabs[nextIndex]);
            break;
        case 'ArrowUp':
            if (!vertical) {
                return;
            }
            event.preventDefault();
            nextIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
            setTabFocus(tablist, tabs[nextIndex]);
            break;
        case 'ArrowDown':
            if (!vertical) {
                return;
            }
            event.preventDefault();
            nextIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
            setTabFocus(tablist, tabs[nextIndex]);
            break;
        case 'Home':
            event.preventDefault();
            setTabFocus(tablist, tabs[0]);
            break;
        case 'End':
            event.preventDefault();
            setTabFocus(tablist, tabs[tabs.length - 1]);
            break;
        case 'Enter':
        case ' ':
            event.preventDefault();
            event.target.click();
            break;
        default:
            break;
    }
}

function initTablist(tablist) {
    const tabs = getTabs(tablist);

    if (!tabs.length) {
        return;
    }

    setSelectedTab(tablist, getSelectedTab(tabs));

    tablist.addEventListener('keydown', (event) => handleTablistKeydown(event, tablist));

    tabs.forEach((tab) => {
        tab.addEventListener('shown.bs.tab', () => {
            setSelectedTab(tablist, tab);
        });
    });
}

export function initAccessibleTabs() {
    document.querySelectorAll('[role="tablist"]').forEach(initTablist);
}

onReady(initAccessibleTabs);
